import { v4 as generateUuid } from 'uuid'

import { hashPrefixed } from '../../../lib/utils'
import { EmbroideryItemCustomProperties, EmbroiderySelections, LogoFile } from '../types'

import { deriveNameFromEmbroideryPlacement } from './deriveNameFromEmbroideryPlacement'

export function generateEmbroideryLineItemProperties(
  options: EmbroiderySelections | undefined,
  productColorHex: string | undefined,
  shopifyId: number | undefined,
  logoSetupId?: number | undefined
): EmbroideryItemCustomProperties {
  if (!options) return {}
  const {
    iconDesignFile,
    iconDesignName,
    iconInitials,
    iconInitialsColor,
    iconImageSource,
    iconPlacement,
    logoName,
    logoDesignFile, // B2B file
    logoFile, // DTC file
    logoPlacement,
    logoImageSource,
    textColor,
    textFirstLine,
    textFont,
    textPlacement,
    textSecondLine,
    logoId,
    logoPreview,
    logoColor,
    logoNote,
    manualHandling,
  } = options

  const allProperties: EmbroideryItemCustomProperties = {
    _guid: generateUuid(),
    text_first_line: textFirstLine,
    text_second_line: textSecondLine,
    text_color: textColor?.designFile,
    _text_thread_color_value: textColor?.hexCode,
    text_placement: textPlacement ? deriveNameFromEmbroideryPlacement(textPlacement) : undefined,
    text_font: textFont?.key,
    icon_placement: iconPlacement ? deriveNameFromEmbroideryPlacement(iconPlacement) : undefined,
    icon_design: iconDesignName,
    icon_initials: iconInitials,
    icon_initials_color: iconInitials && iconInitialsColor?.designFile,
    _icon_thread_color_value: iconInitialsColor?.hexCode,
    _icon_file_name: iconDesignFile,
    _text_font_family_value: textFont?.designFile,
    _icon_font_family_value: iconInitials ? 'Helvetica' : undefined,
    _icon_image_source: iconImageSource,
    _logo_file_name: logoDesignFile ?? logoFile?.filename ?? undefined,
    _logo_image_source: logoImageSource,
    _logo_id: logoId,
    logo_placement: logoPlacement ? deriveNameFromEmbroideryPlacement(logoPlacement) : undefined,
    _logo_preview: logoPreview,
    logo_name: logoName,
    logo_color_option: logoColor,
    logo_note: logoNote,
    _LPROP: 'final sale',
    _embroideryId: shopifyId?.toString(),
    _scrub_color: hashPrefixed(productColorHex),
    _embroidery_version: '1.5',
    _logo_setup_embroideryId: logoSetupId?.toString(),
    _logo_setup_guid: logoSetupId ? generateUuid() : undefined,
    _logo_manual_handling: deriveLogoManualHandling(manualHandling, logoFile),
  }

  return removeUnusedProperties(allProperties)
}

/**
 * Pass through the explicit `manualHandling` selection if it exists (gift portal / B2B),
 * otherwise use the DTC logo file's backend-provided value. If the logo file has no
 * value, default to true so the logo still gets reviewed. If there is no logo
 * file at all, return undefined — this is either non-logo embroidery or a B2B logo,
 * which carries its value on `manualHandling` instead.
 */
export function deriveLogoManualHandling(
  manualHandling: boolean | undefined,
  logoFile: LogoFile | undefined
): 'true' | 'false' | undefined {
  if (manualHandling !== undefined) return manualHandling ? 'true' : 'false'
  if (!logoFile) return undefined
  return logoFile.manualHandling ?? true ? 'true' : 'false'
}

export function removeUnusedProperties<T extends EmbroideryItemCustomProperties>(o: T): T {
  return Object.entries(o).reduce((accumulator, [key, val]) => {
    return val ? { ...accumulator, [key]: val } : accumulator
  }, {} as T)
}
